import React, { createContext, useContext, useState, useEffect } from 'react';

// Standard Currency Symbols & Details
export const CURRENCY_MAP = {
    USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸', code: 'USD' },
    CAD: { symbol: '$', name: 'Canadian Dollar', flag: '🇨🇦', code: 'CAD' },
    EUR: { symbol: '€', name: 'Euro', flag: '🇪🇺', code: 'EUR' },
    GBP: { symbol: '£', name: 'British Pound', flag: '🇬🇧', code: 'GBP' },
    AUD: { symbol: '$', name: 'Australian Dollar', flag: '🇦🇺', code: 'AUD' },
    JPY: { symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', code: 'JPY' },
    CHF: { symbol: 'CHF ', name: 'Swiss Franc', flag: '🇨🇭', code: 'CHF' },
    NZD: { symbol: '$', name: 'New Zealand Dollar', flag: '🇳🇿', code: 'NZD' },
    MXN: { symbol: '$', name: 'Mexican Peso', flag: '🇲🇽', code: 'MXN' }
};

// Default exchange rate multipliers (relative to USD)
export const DEFAULT_RATES = {
    USD: 1.0,
    CAD: 1.36,
    EUR: 0.91,
    GBP: 0.77,
    AUD: 1.49,
    JPY: 155.0,
    CHF: 0.89,
    NZD: 1.63,
    MXN: 18.2
};

export const convertPrice = (amount, activeCurrency = 'USD', rates = DEFAULT_RATES, fromCurr = 'USD') => {
    const numericAmount = parseFloat(amount) || 0;
    if (activeCurrency === fromCurr) return numericAmount;
    const fromRate = rates[fromCurr] || 1.0;
    const toRate = rates[activeCurrency] || 1.0;
    return (numericAmount / fromRate) * toRate;
};

export const formatPrice = (amount, activeCurrency = 'USD', rates = DEFAULT_RATES, fromCurr = 'USD', options = {}) => {
    const converted = convertPrice(amount, activeCurrency, rates, fromCurr);
    const currInfo = CURRENCY_MAP[activeCurrency] || { symbol: '$', code: activeCurrency };
    const decimals = activeCurrency === 'JPY' ? 0 : 2;
    const formattedNumber = converted.toFixed(decimals);
    if (options.showCode) {
        return `${currInfo.symbol}${formattedNumber} ${currInfo.code}`;
    }
    return `${currInfo.symbol}${formattedNumber}`;
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children, settings = {} }) => {
    // Read multi-currency settings
    const enabled = settings.currencyEngineEnabled !== false && settings.currencyEngineEnabled !== 'false';
    const baseCurrency = (settings.baseCurrency || 'USD').toUpperCase();
    const enabledCurrencies = Array.isArray(settings.enabledCurrencies) && settings.enabledCurrencies.length > 0
        ? settings.enabledCurrencies.map(c => String(c).toUpperCase())
        : ['USD', 'CAD', 'EUR', 'GBP'];

    const customRates = settings.currencyRates || {};
    const rates = { ...DEFAULT_RATES, ...customRates };

    // Active currency state with localStorage persistence
    const [activeCurrency, setActiveCurrency] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('omnivaso_currency');
            if (saved && enabledCurrencies.includes(saved.toUpperCase())) {
                return saved.toUpperCase();
            }
        }
        return baseCurrency;
    });

    useEffect(() => {
        if (activeCurrency && typeof window !== 'undefined') {
            localStorage.setItem('omnivaso_currency', activeCurrency);
        }
    }, [activeCurrency]);

    // Keep active currency valid if settings change
    useEffect(() => {
        if (!enabledCurrencies.includes(activeCurrency)) {
            setActiveCurrency(baseCurrency);
        }
    }, [baseCurrency, enabledCurrencies.join(',')]);

    // Price conversion function
    const convertPrice = (amount, fromCurr = baseCurrency) => {
        const numericAmount = parseFloat(amount) || 0;
        if (!enabled || activeCurrency === fromCurr) {
            return numericAmount;
        }

        const fromRate = rates[fromCurr] || 1.0;
        const toRate = rates[activeCurrency] || 1.0;

        // Convert from base currency or cross rate
        const inUsd = numericAmount / fromRate;
        const converted = inUsd * toRate;
        return converted;
    };

    // Price formatting function
    const formatPrice = (amount, fromCurr = baseCurrency, options = {}) => {
        const numericAmount = parseFloat(amount) || 0;
        const converted = convertPrice(numericAmount, fromCurr);
        const currInfo = CURRENCY_MAP[activeCurrency] || { symbol: '$', code: activeCurrency };

        const showCode = options.showCode !== undefined ? options.showCode : (settings.currencyShowCode !== false && settings.currencyShowCode !== 'false');
        const decimals = activeCurrency === 'JPY' ? 0 : 2;

        const formattedNumber = converted.toFixed(decimals);

        if (showCode) {
            return `${currInfo.symbol}${formattedNumber} ${currInfo.code}`;
        }
        return `${currInfo.symbol}${formattedNumber}`;
    };

    return (
        <CurrencyContext.Provider value={{
            enabled,
            baseCurrency,
            activeCurrency,
            setActiveCurrency,
            enabledCurrencies,
            rates,
            convertPrice,
            formatPrice,
            currencyInfo: CURRENCY_MAP[activeCurrency] || CURRENCY_MAP.USD
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        // Fallback for components outside provider
        return {
            enabled: false,
            baseCurrency: 'USD',
            activeCurrency: 'USD',
            setActiveCurrency: () => {},
            enabledCurrencies: ['USD', 'CAD', 'EUR', 'GBP'],
            rates: DEFAULT_RATES,
            convertPrice: (amt) => parseFloat(amt) || 0,
            formatPrice: (amt) => `$${(parseFloat(amt) || 0).toFixed(2)}`,
            currencyInfo: CURRENCY_MAP.USD
        };
    }
    return ctx;
};
